// Consent and privacy tools shared by the welcome screen, the sign-up form and the settings: links to the legal pages,
// the sentence accepted at sign-up, the gate shown to an account that has not accepted the current terms, and the
// dialog that deletes the account. The texts are in i18n/<lang>/legal.ts.
import { currentUser, logout, onAuthChange } from '../data/api.js';
import { pullMe } from '../data/sync.js';
import { acceptTerms, deleteMyAccount, needsConsent } from '../data/legal.js';
import { esc, modal, on, toast } from './dom.js';
import { currentPath, refresh } from './router.js';
import { t } from '../i18n/index.js';
import { errorText } from '../i18n/content.js';

export const LEGAL_PAGES = ['terms', 'privacy', 'notice'] as const;
export type LegalPageId = (typeof LEGAL_PAGES)[number];

/** `blank`: the page opens in a new tab, so a form or a modal being filled in is not lost. */
const pageLink = (p: LegalPageId, label: string, blank: boolean) => `<a href="#/legal/${p}" ${blank ? 'target="_blank" rel="noopener"' : ''}>${esc(label)}</a>`;

/** "Terms of use · Privacy policy · Legal notice". */
export const legalLinks = (blank = false): string => `<span class="legal-links">${LEGAL_PAGES.map(p => pageLink(p, t.legal.pages[p], blank)).join('')}</span>`;

/** The sentence next to the consent checkbox, with the two pages linked (new tab). */
export const acceptSentence = (): string => {
  const L = t.legal;
  return `${esc(L.acceptPrefix)}${pageLink('terms', L.pages.terms, true)}${esc(L.acceptJoin)}${pageLink('privacy', L.pages.privacy, true)}${esc(L.acceptHealth)}`;
};

let gateOpen = false, verified = false, verifiedFor: string | null = null;
// another account, or none: the next route reads the account again. A token refresh or a profile sync also notifies
// auth listeners; re-reading on those made every route pull the account, racing the upload of a change made just before.
onAuthChange(s => { if (s?.user.id !== verifiedFor) { verified = false; verifiedFor = s?.user.id ?? null; } });
/** The legal pages are readable without consenting: the gate would hide the very texts it asks to read. */
export const readingTerms = (): boolean => currentPath().startsWith('/legal');

/**
 * Makes sure the signed-in account accepted the current terms; otherwise asks, without a way around (accept, or sign
 * out). Resolves true when the player may go on. The cached session may be older than the account (terms bumped
 * since the last visit): unless the caller just synced (`fresh`), the account is read from the server once per session.
 * Called at start-up and on every route change (main.ts), so it must stay cheap when there is nothing to ask.
 */
export async function consentGate(fresh = false): Promise<boolean> {
  if (gateOpen || readingTerms()) return false;
  if (!fresh && !verified) {
    try { await pullMe(); verified = true; } catch { return true; } // offline: the gate waits for the next start rather than blocking the app
    if (readingTerms()) return false; // the player moved to a legal page while the account was loading
  }
  if (!needsConsent()) return true;
  gateOpen = true;
  const L = t.legal, first = !currentUser()?.termsVersion;
  return new Promise<boolean>(res => {
    const m = modal(`<h2>${esc(L.consentTitle)}</h2><p class="mut">${esc(first ? L.consentIntroNew : L.consentIntroChanged)}</p>
      <p>${legalLinks(true)}</p>
      <label class="consent-check mt-3"><input type="checkbox" data-consent-check><span>${acceptSentence()}</span></label>
      <div class="actions mt-3"><button data-consent-decline>${esc(L.consentDecline)}</button><button class="primary" data-consent-accept disabled>${esc(L.consentAccept)}</button></div>`, { dismissable: false });
    const root = m.el as HTMLElement;
    const box = root.querySelector<HTMLInputElement>('[data-consent-check]')!, ok = root.querySelector<HTMLButtonElement>('[data-consent-accept]')!;
    const done = (v: boolean) => { gateOpen = false; m.close(); res(v); };
    box.onchange = () => { ok.disabled = !box.checked; };
    on(root, 'click', '[data-consent-accept]', async () => {
      try { await acceptTerms(); toast(L.consentSaved, 'good'); done(true); } catch (e) { toast(errorText((e as Error).message), 'bad'); }
    });
    on(root, 'click', '[data-consent-decline]', async () => { await logout(); done(false); refresh(); });
  });
}

/** Deletion of the account: a checkbox stands between the player and the irreversible button. */
export function deleteAccountDialog(): Promise<void> {
  const L = t.legal;
  return new Promise<void>(res => {
    const m = modal(`<h2>${esc(L.deleteTitle)}</h2><p class="mut">${esc(L.deleteText)}</p>
      <label class="consent-check mt-3"><input type="checkbox" data-delete-check><span>${esc(L.deleteCheck)}</span></label>
      <div class="actions mt-3"><button data-close>${esc(t.common.cancel)}</button><button class="danger" data-delete-go disabled>${esc(L.deleteConfirm)}</button></div>`, { onClose: () => res() });
    const root = m.el as HTMLElement;
    const box = root.querySelector<HTMLInputElement>('[data-delete-check]')!, go = root.querySelector<HTMLButtonElement>('[data-delete-go]')!;
    box.onchange = () => { go.disabled = !box.checked; };
    on(root, 'click', '[data-delete-go]', async () => {
      try { await deleteMyAccount(); m.close(); toast(L.deleteDone, 'good'); refresh(); } catch (e) { toast(errorText((e as Error).message), 'bad'); }
    });
  });
}
